/**
 * Galaxy / fluid field renderer.
 *
 * Particles ride logarithmic spiral arms with differential rotation (inner
 * orbits sweep faster than outer ones), which is what winds the arms and gives
 * the disc its shear. On top of that orbit each particle carries an offset
 * driven by a curl-noise field, so the dust behaves like a fluid instead of a
 * rigid pinwheel. A spring pulls every offset back toward zero, which is what
 * lets the structure survive both the turbulence and the cursor wake.
 *
 * Deliberately dependency-free 2D canvas: at these particle counts the cost is
 * dominated by drawImage of pre-rendered glow sprites, which the compositor
 * handles far better than building a gradient per particle per frame.
 */

const TAU = Math.PI * 2;

/* --- cheap value noise; we need smooth and repeatable, not high quality --- */

function hash(x, y) {
  let h = (x | 0) * 374761393 + (y | 0) * 668265263;
  h = (h ^ (h >> 13)) * 1274126177;
  return ((h ^ (h >> 16)) >>> 0) / 4294967295;
}

const smooth = (t) => t * t * (3 - 2 * t);

function noise2(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const u = smooth(x - xi);
  const v = smooth(y - yi);
  const a = hash(xi, yi);
  const b = hash(xi + 1, yi);
  const c = hash(xi, yi + 1);
  const d = hash(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

/**
 * Curl of the noise field. Taking the perpendicular of the gradient gives a
 * divergence-free field, so particles swirl and never pile up in sinks. That
 * is the whole reason this reads as fluid rather than as drifting confetti.
 */
function curl(x, y, out) {
  const e = 0.75;
  const dy = (noise2(x, y + e) - noise2(x, y - e)) / (2 * e);
  const dx = (noise2(x + e, y) - noise2(x - e, y)) / (2 * e);
  out[0] = dy;
  out[1] = -dx;
}

/* Core is warm; the disc cools outward through violet into cyan. */
const TINTS = [
  [255, 236, 200],
  [255, 198, 140],
  [196, 150, 255],
  [140, 130, 255],
  [110, 190, 255],
];

function makeSprite(rgb, size) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  const half = size / 2;
  const grad = g.createRadialGradient(half, half, 0, half, half, half);
  const [r, gr, b] = rgb;
  grad.addColorStop(0, 'rgba(' + r + ',' + gr + ',' + b + ',1)');
  grad.addColorStop(0.25, 'rgba(' + r + ',' + gr + ',' + b + ',0.55)');
  grad.addColorStop(0.55, 'rgba(' + r + ',' + gr + ',' + b + ',0.14)');
  grad.addColorStop(1, 'rgba(' + r + ',' + gr + ',' + b + ',0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return c;
}

export function createGalaxy(canvas, options = {}) {
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return { destroy() {} };

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const opts = {
    arms: 3,
    tilt: -0.32,
    squash: 0.46,
    background: '#05050b',
    ...options,
  };

  const sprites = TINTS.map((t) => makeSprite(t, 32));
  const force = [0, 0];

  let width = 0;
  let height = 0;
  let dpr = 1;
  let count = 0;
  let running = false;
  let visible = true;
  let frame = 0;
  let last = 0;
  let time = 0;

  // Structure-of-arrays: one contiguous Float32Array per attribute keeps the
  // per-frame loop free of property lookups and object churn.
  let radius, angle, omega, offX, offY, velX, velY, size, alpha, tint;

  let nebula = null;
  let stars = null;
  // Particles are drawn into a persistence buffer that fades a little each
  // frame. The leftover smear along each orbit is what turns discrete dots
  // into flowing dust, and it costs one extra composite instead of 10x the
  // particle count.
  let trail = null;
  let trailCtx = null;

  const pointer = { x: 0, y: 0, strength: 0 };

  function seed() {
    const area = width * height;
    count = Math.max(1600, Math.min(6500, Math.round(area / 260)));

    radius = new Float32Array(count);
    angle = new Float32Array(count);
    omega = new Float32Array(count);
    offX = new Float32Array(count);
    offY = new Float32Array(count);
    velX = new Float32Array(count);
    velY = new Float32Array(count);
    size = new Float32Array(count);
    alpha = new Float32Array(count);
    tint = new Uint8Array(count);

    const maxR = Math.hypot(width, height) * 0.36;
    const innerR = 0.06 * maxR;
    // Winding constant of the log spiral r = innerR * e^(k*theta).
    const k = 0.42;

    for (let i = 0; i < count; i++) {
      // sqrt keeps the disc from clumping in the middle under uniform sampling
      const t = Math.sqrt(Math.random());
      const r = innerR + t * maxR;

      // Roughly a third of the disc is inter-arm field stars. Without them the
      // arms read as bare strings of beads instead of dust lanes in a disc.
      const inArm = Math.random() > 0.34;
      const arm = Math.floor(Math.random() * opts.arms);
      const armAngle = Math.log(r / innerR) / k;
      // Two uniforms summed approximate a gaussian, giving each arm a dense
      // ridge that frays at the edges instead of a flat-topped smear.
      const width_ = 0.16 + 0.6 * t;
      const spread = (Math.random() + Math.random() - 1) * width_;
      const bulge = t < 0.12 ? Math.random() * TAU : 0;

      radius[i] = r;
      angle[i] = inArm
        ? armAngle + (arm * TAU) / opts.arms + spread + bulge
        : Math.random() * TAU;

      // Falling rotation curve: inner orbits sweep faster, so arms wind.
      omega[i] = 0.42 / (Math.pow(r / maxR, 0.72) * 9 + 0.85);

      const depth = 0.4 + Math.random() * 0.6;
      // Particles sitting on the arm ridge burn brighter than the strays.
      const ridge = inArm ? 1 - Math.min(1, Math.abs(spread) / width_) : 0;
      size[i] = (0.6 + Math.random() * Math.random() * 3.2) * depth;
      alpha[i] =
        (inArm ? 0.14 + Math.random() * 0.36 + ridge * 0.46 : 0.07 + Math.random() * 0.2) *
        depth;

      // Colour by radius, jittered so the bands blend instead of banding.
      tint[i] = Math.min(
        TINTS.length - 1,
        Math.max(0, Math.round(t * 4.6 + (Math.random() - 0.5) * 1.1))
      );
    }
  }

  /** Big soft clouds, painted once at low resolution and stretched back up. */
  function buildNebula() {
    const w = Math.max(1, Math.round(width / 6));
    const h = Math.max(1, Math.round(height / 6));
    nebula = document.createElement('canvas');
    nebula.width = w;
    nebula.height = h;
    const g = nebula.getContext('2d');
    g.globalCompositeOperation = 'lighter';

    const blobs = [
      [0.62, 0.5, 0.3, '124,92,255', 0.34],
      [0.44, 0.42, 0.22, '0,150,220', 0.26],
      [0.62, 0.5, 0.14, '255,168,110', 0.22],
      [0.78, 0.64, 0.18, '190,90,255', 0.2],
      [0.3, 0.66, 0.2, '70,60,190', 0.16],
    ];

    for (let i = 0; i < blobs.length; i++) {
      const bx = blobs[i][0];
      const by = blobs[i][1];
      const br = blobs[i][2];
      const rgb = blobs[i][3];
      const a = blobs[i][4];
      const px = bx * w;
      const py = by * h;
      const r = br * Math.max(w, h);
      const grad = g.createRadialGradient(px, py, 0, px, py, r);
      grad.addColorStop(0, 'rgba(' + rgb + ',' + a + ')');
      grad.addColorStop(0.5, 'rgba(' + rgb + ',' + a * 0.35 + ')');
      grad.addColorStop(1, 'rgba(' + rgb + ',0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, w, h);
    }
  }

  /** Static starfield, blitted whole rather than re-plotted every frame. */
  function buildStars() {
    stars = document.createElement('canvas');
    stars.width = Math.max(1, Math.round(width * dpr));
    stars.height = Math.max(1, Math.round(height * dpr));
    const g = stars.getContext('2d');
    g.scale(dpr, dpr);
    const n = Math.round((width * height) / 5200);
    for (let i = 0; i < n; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const r = Math.random() * 0.9 + 0.25;
      const a = Math.random() * 0.5 + 0.12;
      g.fillStyle = 'rgba(226,232,255,' + a + ')';
      g.beginPath();
      g.arc(x, y, r, 0, TAU);
      g.fill();
    }
  }

  function resize() {
    const rect = canvas.getBoundingClientRect();
    const nextW = Math.max(1, Math.round(rect.width));
    const nextH = Math.max(1, Math.round(rect.height));
    // Capping DPR keeps fill cost sane on phones and 4K displays alike.
    const nextDpr = Math.min(window.devicePixelRatio || 1, 1.6);
    if (nextW === width && nextH === height && nextDpr === dpr) return;

    width = nextW;
    height = nextH;
    dpr = nextDpr;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    trail = document.createElement('canvas');
    trail.width = canvas.width;
    trail.height = canvas.height;
    trailCtx = trail.getContext('2d');
    trailCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    seed();
    buildNebula();
    buildStars();
  }

  function draw(dt) {
    const fade = 1 - Math.exp(-2.4 * dt);
    const cx = width * (width < 760 ? 0.5 : 0.7);
    const cy = height * (width < 760 ? 0.66 : 0.5);
    const cos = Math.cos(opts.tilt);
    const sin = Math.sin(opts.tilt);

    // Fade the persistence buffer rather than clearing it. Framerate-independent
    // so trails are the same length on a 60Hz and a 144Hz display.
    trailCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    trailCtx.globalCompositeOperation = 'destination-out';
    trailCtx.globalAlpha = fade;
    trailCtx.fillStyle = '#000';
    trailCtx.fillRect(0, 0, width, height);
    trailCtx.globalCompositeOperation = 'lighter';

    const noiseScale = 0.0032;
    const drift = time * 0.06;

    for (let i = 0; i < count; i++) {
      angle[i] += omega[i] * dt;

      const r = radius[i];
      const a = angle[i];
      // Orbit in the disc plane, then tilt it into a three-quarter view.
      const dx = Math.cos(a) * r;
      const dy = Math.sin(a) * r * opts.squash;
      const bx = cx + dx * cos - dy * sin;
      const by = cy + dx * sin + dy * cos;

      curl(bx * noiseScale + drift, by * noiseScale, force);

      let vx = velX[i] + force[0] * 26 * dt;
      let vy = velY[i] + force[1] * 26 * dt;

      if (pointer.strength > 0.001) {
        const px = bx + offX[i] - pointer.x;
        const py = by + offY[i] - pointer.y;
        const d2 = px * px + py * py;
        if (d2 < 52900) {
          const d = Math.sqrt(d2) || 1;
          // Falls off with distance: the wake is a push, not a hard shove.
          const push = (1 - d / 230) * pointer.strength * 240;
          vx += (px / d) * push * dt;
          vy += (py / d) * push * dt;
        }
      }

      // Spring back toward the orbit, plus damping: the disc always reassembles.
      vx += -offX[i] * 3.1 * dt;
      vy += -offY[i] * 3.1 * dt;
      const damp = Math.exp(-1.9 * dt);
      vx *= damp;
      vy *= damp;

      offX[i] += vx * dt;
      offY[i] += vy * dt;
      velX[i] = vx;
      velY[i] = vy;

      const x = bx + offX[i];
      const y = by + offY[i];
      if (x < -40 || x > width + 40 || y < -40 || y > height + 40) continue;

      // Dust lanes: modulating alpha by the same field carves dark gaps.
      const lane = 0.55 + 0.45 * noise2(bx * 0.0016 + drift * 0.5, by * 0.0016);
      const s = size[i] * 5.2;
      trailCtx.globalAlpha = alpha[i] * lane;
      trailCtx.drawImage(sprites[tint[i]], x - s / 2, y - s / 2, s, s);
    }

    // Composite the frame: sky, then clouds, then the dust, then the core on top.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = opts.background;
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(stars, 0, 0, width, height);

    ctx.globalCompositeOperation = 'lighter';
    if (nebula) {
      ctx.globalAlpha = 0.62;
      ctx.drawImage(nebula, 0, 0, width, height);
    }

    ctx.globalAlpha = 1;
    ctx.drawImage(trail, 0, 0, width, height);

    ctx.globalAlpha = 0.6;
    ctx.drawImage(sprites[0], cx - 75, cy - 75, 150, 150);
    ctx.globalAlpha = 0.2;
    ctx.drawImage(sprites[1], cx - 230, cy - 230, 460, 460);

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  function tick(now) {
    if (!running) return;
    frame = requestAnimationFrame(tick);
    if (!visible) {
      last = now;
      return;
    }
    // Clamp so a backgrounded tab does not resume with one enormous step.
    const dt = Math.min((now - last) / 1000 || 0, 0.05);
    last = now;
    time += dt;
    pointer.strength *= Math.exp(-2.4 * dt);
    draw(dt);
  }

  let paused = false;

  function start() {
    if (running || reduceMotion || paused) return;
    running = true;
    last = performance.now();
    frame = requestAnimationFrame(tick);
  }

  function stop() {
    running = false;
    cancelAnimationFrame(frame);
  }

  function onPointerMove(e) {
    const rect = canvas.getBoundingClientRect();
    pointer.x = e.clientX - rect.left;
    pointer.y = e.clientY - rect.top;
    pointer.strength = Math.min(1, pointer.strength + 0.5);
  }

  function onVisibility() {
    visible = !document.hidden;
  }

  function onResize() {
    resize();
    if (reduceMotion || paused) {
      for (let i = 0; i < 90; i++) draw(1 / 60);
    }
  }

  resize();

  if (reduceMotion) {
    // No loop, but the persistence buffer still has to be filled or the disc
    // would render as bare dots. Wind the sim forward, then paint once.
    for (let i = 0; i < 90; i++) draw(1 / 60);
  } else {
    start();
  }

  window.addEventListener('resize', onResize, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pointermove', onPointerMove, { passive: true });

  let observer = null;
  if (typeof IntersectionObserver === 'function' && !reduceMotion) {
    // Stop burning frames once the hero is scrolled past.
    observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) start();
        else stop();
      },
      { threshold: 0 }
    );
    observer.observe(canvas);
  }

  return {
    setPaused(value) {
      paused = value;
      if (paused) stop();
      else start();
    },
    destroy() {
      stop();
      if (observer) observer.disconnect();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onPointerMove);
    },
  };
}
